import React, { ChangeEvent, useRef, useState } from 'react';
import {
    ModalHeader,
    ModalBody,
    FormGroup,
    Row,
    Col,
    Button,
} from 'reactstrap';
import createNotification from '../../../../../utils/createNotification';
import { Field, Form } from '@availity/form';
import * as yup from 'yup';
import ButtonLoader from '../../../../../components/Layout/Buttons/ButtonLoader';
import Rodal from '../../../../../components/Rodal/Rodal';
import { useStateSelector } from '../../../../../store/selectors';
import SelectFileInputAvaility from '../../../../../components/SelectFileInputAvaility/SelectFileInputAvaility';
import './AddSampleTemplateModal.scss';

interface AddSampleTemplateModalProps {
    isOpened: boolean;
    onClose: () => void;
    afterSuccessfulAdd: () => void;
}

interface FormFields {
    name: string;
    sourceFile: string;
}

export const AddSampleTemplateModal = (props: AddSampleTemplateModalProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const modalForm = useRef(null);
    const [attachedFile, setAttachedFile] = useState<File>(null);

    const saveTemplate = (fields: FormFields) => {
        if (!attachedFile) {
            createNotification('Please select a file to upload.', 'error');
            return;
        }

        setIsSaving(true);

        const formData = new FormData();
        formData.append('File', attachedFile);
        formData.append('Name', fields.name);

        const MAX_FILE_SIZE = 500 * 1024 * 1024;

        axios
            .post('/api/Marketplace/MarketplaceSampleTemplates', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 600000,
                maxContentLength: MAX_FILE_SIZE,
                maxBodyLength: MAX_FILE_SIZE,
            })
            .then((response: any) => {
                if (response.status === 200) {
                    createNotification(
                        'Template has been successfully uploaded.',
                        'success'
                    );
                    props.afterSuccessfulAdd();
                } else {
                    createNotification(
                        response.response?.data?.detail ??
                            'Unexpected error occurred while uploading template.',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'Unexpected error occurred while uploading template.',
                    'error'
                );
            })
            .finally(() => setIsSaving(false));
    };

    const changeFileHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const newFile = event.target.files[0];
        if (newFile) {
            setAttachedFile(newFile);
            modalForm.current.setFieldValue('sourceFile', newFile.name, true);
        }
    };

    const renderForm = () => {
        return (
            <div>
                <Form
                    initialValues={{
                        name: '',
                        sourceFile: '',
                    }}
                    onSubmit={(fields) => saveTemplate(fields)}
                    validationSchema={yup.object().shape({
                        sourceFile: yup.string().required('File is required'),
                        name: yup.string().required('File name is required'),
                    })}
                    innerRef={modalForm}>
                    <FormGroup className="template-inputs-form-group">
                        <Row>
                            <Col md={12}>
                                <SelectFileInputAvaility
                                    attachedFile={attachedFile}
                                    onFileChange={changeFileHandler}
                                    disabled={false}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <label>File Name</label>
                                <Field
                                    name="name"
                                    type="text"
                                    className="form-control"
                                />
                            </Col>
                        </Row>
                    </FormGroup>
                </Form>
                <div className="footer">
                    <Button
                        className="cancel btn btn-secondary"
                        onClick={() => props.onClose()}>
                        Cancel
                    </Button>
                    <ButtonLoader
                        type="submit"
                        buttonText={'Upload'}
                        disabled={false}
                        isLoading={isSaving}
                        onClick={() => {
                            modalForm.current.handleSubmit();
                        }}
                        className={'btn btn-primary loader-submit'}
                        loaderButtonText={''}></ButtonLoader>
                </div>
            </div>
        );
    };

    return props.isOpened ? (
        <div className="add-sample-template-modal">
            <Rodal
                width={500}
                height={300}
                visible={props.isOpened}
                animation={'rotate'}
                onClose={() => props.onClose()}
                showMask={false}>
                <ModalHeader>Upload Document</ModalHeader>
                <ModalBody>{renderForm()}</ModalBody>
            </Rodal>
        </div>
    ) : (
        <></>
    );
};
